import { ReactNode, useContext } from "react";
import { AppContext } from "../store";
import Loading from "../common/Loading";
import { Accordion, Button, Card, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import EventDetail from "./interfaces/EventDetail";
import { EventDetailsDisplay, getKeyValue } from "./displayValues";
import { IObjectKeys } from "./interfaces/ObjectKeys";
import { EventStakeholder } from "./interfaces/EventStakeholder";
import Order from "../pages/types/Order";
import JoinCTA from "../common/JoinCTA";
import NewOrderButton from "../orders/components/newOrderButton";
import ShowToRole from "../common/ShowToRole";
import { Link } from "react-router-dom";

const EventDetails = () => {
  const navigate = useNavigate();
  const { state } = useContext(AppContext);
  let eventId=state.eventInfo.currentEvent?.eventId
  const isVolunteer = state.orgInfo?.volunteer_access;
  const isEC = state.orgInfo?.event_coordinator;
  const isFS = state.orgInfo?.field_support;
  const loading = state.orgInfo?.loading || state.eventInfo?.currentEvent?.loading;
  const orders = state.eventInfo.orders.filter((o) => o.eventId === eventId);
  const stakeholders = state.eventInfo?.currentEvent?.stakeholders || [];
  const display = (event: IObjectKeys, section: "overview" | "projection" | "financial" | "report") => {
    const content = EventDetailsDisplay[section];
    const body = Object.keys(content)
      .filter((k: string) => !!getKeyValue(event, k))
      .map((k: string) => {
        return (
          <tr key={k}>
            <th>{getKeyValue(content, k) as ReactNode}</th>
            <td>{getKeyValue(event, k) as ReactNode}</td>
          </tr>
        );
      });
    return (
      body.length && (
        <Table striped hover size="sm">
          <tbody>{body}</tbody>
        </Table>
      )
    );
  };
  const displayRole = (role: "V" | "EC" | "RD") => {
    const displayValues = {
      V: "Volunteer",
      EC: "Event Coordinator",
      RD: "Regional Director",
    };
    return displayValues[role];
  };
  const displayDetails = (event: EventDetail) => {
    const local = { ...event } as any;
    delete local.__typename;
    const overview = display(event, "overview");
    return (
      <Accordion defaultActiveKey="1" className="mb-3">
        <Accordion.Item eventKey="0">
          <Accordion.Header>Overview</Accordion.Header>
          <Accordion.Body>{overview}</Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="1">
            <Accordion.Header>Report</Accordion.Header>
            <Accordion.Body><Link to={`/reports/event/`} state={{ eventId }}>Event History and Planning Sheet</Link></Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };
  const displayEvent = (event: EventDetail) => {
    return (
      event && (
        <>
          <h1 className="mb-5">{event.EventName}</h1>
          {displayDetails(event)}

          {stakeholders && displayOrdering(stakeholders as EventStakeholder[], event)}

          {event && displayMailPull(event)}
        </>
      )
    );
  };
  const displayMailPull = (event: EventDetail) => {
    return (
      event && (
        <ShowToRole allowedRoles={["Staff"]}>
          <Card className="mt-3">
            <Card.Header>Send Mail for {event.EventName}</Card.Header>
            <Card.Body>Build and download contact lists to send mailings.</Card.Body>
            <Card.Footer>
              {
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={event.Active !== "Yes"}
                  onClick={() => {
                    navigate(`/event/mail-pull/${eventId}`);
                  }}
                >
                  {event.Active !== "Yes" ? "Mail Pull Disabled" : "Create a Contact List"}
                </Button>
              }
            </Card.Footer>
          </Card>
        </ShowToRole>
      )
    );
  };
  const displayOrdering = (eventStakeholders: EventStakeholder[], event: EventDetail) => {
    if (!!eventStakeholders?.length && event) {
      return (
        <Card>
          <Card.Header>Ordering Merchandise for {event.EventName}</Card.Header>
          <Card.Body>
            <p className="mb-2">This process will notify:</p>
            {
              <ul className="mb-4">
                {eventStakeholders.map((s: EventStakeholder) => (
                  <li key={s.email}>
                    <b>{s.name}</b>, {displayRole(s.role)} (<a href="mailto:{s.email}">{s.email}</a>)
                  </li>
                ))}
              </ul>
            }
            <p className="mb-2">Orders:</p>
            {orders && orders.length > 0 && (
              <ul>
                {orders.map((o: Order) => (
                  <li key={o.id}>
                    {o.description}
                    <Button size="sm" variant="link" className="ms-2" onClick={() => navigate(`/order/${o.id}`)}>
                      Edit
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {orders && orders.length === 0 && <p>No orders placed.</p>}
          </Card.Body>
          <Card.Footer>
            <NewOrderButton size="sm" variant="secondary" disabled={event.Active !== "Yes"}>
              {event.Active !== "Yes" ? "Ordering Disabled" : "Start a New Order"}
            </NewOrderButton>
          </Card.Footer>
        </Card>
      );
    } else {
      return (
        <Card>
          <Card.Header>Ordering Merchandise for {event.EventName}</Card.Header>
          <Card.Body>
            Stakeholders not available. To try again, <a href={`/event/${eventId}/`}>Please refresh your browser</a>, or contact support.
          </Card.Body>
          <Card.Footer>
            <NewOrderButton size="sm" variant="secondary" disabled={true}>
              Ordering Disabled
            </NewOrderButton>
          </Card.Footer>
        </Card>
      );
    }
  };
  if (loading) {
    return <Loading />;
  } else if (isVolunteer || isEC || isFS) {
    if (state.eventInfo.currentEventDetail) {
      return displayEvent(state.eventInfo.currentEventDetail);
    }
  } else {
    return <JoinCTA />;
  }
  return null;
};

export default EventDetails;
